'use client';

import { Sparkles } from 'lucide-react';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import type { ActivitySearchMode } from './shared';

type Props = {
    mode: ActivitySearchMode;
    onChange: (mode: ActivitySearchMode) => void;
    /** Visual surface the toggle sits on. `light` = white bar, `glass` = dark hero. */
    theme?: 'light' | 'glass';
    className?: string;
    'data-testid'?: string;
};

/**
 * Single "AI" on/off toggle. Pressing it flips the search surface between the regular filters
 * and the AI composer. Stateless, so the homepage drives it from localStorage and the
 * /teambuildings page from the `?mode=` store.
 */
export const SearchModeToggle: React.FC<Props> = ({
    mode,
    onChange,
    theme = 'light',
    className,
    'data-testid': testId,
}) => {
    const i18n = useI18n();
    const isAi = mode === 'ai';

    return (
        <button
            type="button"
            data-testid={testId}
            onClick={() => onChange(isAi ? 'normal' : 'ai')}
            aria-pressed={isAi}
            aria-label={i18n._('activity.search.mode.ai', undefined, { message: 'AI Search' })}
            className={cn(
                'flex h-10 shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-4xl px-4 text-sm font-bold leading-none outline-none transition-colors lg:h-16 lg:gap-2 lg:px-6 lg:text-base',
                isAi
                    ? 'bg-yellow text-yellow-950 hover:brightness-105'
                    : theme === 'light'
                      ? 'bg-black/5 text-gray-700 hover:bg-black/10'
                      : 'bg-white/10 text-white/85 hover:bg-white/20',
                className,
            )}
        >
            <Sparkles className="size-4 shrink-0" strokeWidth={2.25} aria-hidden />
            AI
        </button>
    );
};
